import { TooltipDirective } from '../../../auxiliares/tooltip/tooltip.directive';
import { BOTONES } from '../../../componentes/botones';
import { Component, forwardRef } from '@angular/core';

import {
  ControlValueAccessor,
  FormArray,
  FormControl,
  FormGroup,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { NgSelectModule } from '@ng-select/ng-select';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { RangoHorario } from 'modelos/src';
import { TimePickerComponent } from '../../../componentes/time-picker/time-picker.component';

interface IRangoHorarioForm {
  dias: FormControl<string[] | null>;
  horarioDesde: FormControl<string | null>;
  horarioHasta: FormControl<string | null>;
}

@Component({
  selector: 'app-rangos-horarios',
  standalone: true,
  imports: [TooltipDirective, 
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    NgSelectModule,
    MatIconModule,
    BOTONES,
    MatCardModule,
    TimePickerComponent,
  ],
  templateUrl: './rangos-horarios.component.html',
  styleUrl: './rangos-horarios.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RangosHorariosComponent),
      multi: true,
    },
  ],
})
export class RangosHorariosComponent implements ControlValueAccessor {
  public rangosArray = new FormArray<FormGroup<IRangoHorarioForm>>([]);

  public diasSemana: string[] = [
    'Lunes',
    'Martes',
    'Miércoles',
    'Jueves',
    'Viernes',
    'Sábado',
    'Domingo',
  ];

  public disabled = false;

  private onChange: (value: RangoHorario[] | null) => void = () => {};
  private onTouched: () => void = () => {};

  constructor() {
    // Suscribirse a cambios en el FormArray
    this.rangosArray.valueChanges.subscribe(() => {
      this.emitChanges();
    });
  }

  // ControlValueAccessor methods
  writeValue(value: RangoHorario[] | null): void {
    this.rangosArray.clear({ emitEvent: false });

    if (value && Array.isArray(value) && value.length > 0) {
      value.forEach((rango) => {
        this.rangosArray.push(this.createRangoFormGroup(rango), {
          emitEvent: false,
        });
      });
    } else {
      // Si no hay rangos, agregar uno vacío
      this.agregarRango();
    }
  }

  registerOnChange(fn: (value: RangoHorario[] | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) {
      this.rangosArray.disable({ emitEvent: false });
    } else {
      this.rangosArray.enable({ emitEvent: false });
    }
  }

  // Métodos del componente
  private createRangoFormGroup(
    rango?: RangoHorario,
  ): FormGroup<IRangoHorarioForm> {
    return new FormGroup({
      dias: new FormControl<string[] | null>(rango?.dias ?? null),
      horarioDesde: new FormControl<string | null>(
        rango?.horarioDesde ?? null,
        [Validators.pattern(/^([01]\d|2[0-3]):([0-5]\d)$/)],
      ),
      horarioHasta: new FormControl<string | null>(
        rango?.horarioHasta ?? null,
        [Validators.pattern(/^([01]\d|2[0-3]):([0-5]\d)$/)],
      ),
    });
  }

  public agregarRango(): void {
    this.rangosArray.push(this.createRangoFormGroup());
    this.emitChanges();
  }

  public eliminarRango(index: number): void {
    this.rangosArray.removeAt(index);
    this.emitChanges();
    this.onTouched();
  }

  private emitChanges(): void {
    const value = this.rangosArray.value as RangoHorario[];
    // Filtrar rangos vacíos (sin datos)
    const filteredValue = value.filter(
      (r) => r.dias?.length || r.horarioDesde || r.horarioHasta,
    );
    this.onChange(filteredValue.length > 0 ? filteredValue : null);
  }

  public onBlur(): void {
    this.onTouched();
  }
}
